import { useState } from 'react'
import { Chip, Button, PriorityChip, EmptyState } from '@/components/ui'
import { campanas } from '@/mock'
import { AlertTriangle, Clock, Users, MessageCircle, Pause, Play, History, Copy, Infinity as InfIcon, CalendarCheck, Rocket } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HistorialDrawer } from './HistorialDrawer'

const tipoConfig = {
  mora: { label: 'Mora', color: 'bg-rose-50 text-rose-700', ring: 'from-rose-100 to-rose-200 text-rose-700', dot: 'bg-rose-500' },
  preventiva: { label: 'Preventiva', color: 'bg-blue-50 text-blue-700', ring: 'from-blue-100 to-blue-200 text-blue-700', dot: 'bg-blue-500' },
  reenvio: { label: 'Reenvío', color: 'bg-violet-50 text-violet-700', ring: 'from-violet-100 to-violet-200 text-violet-700', dot: 'bg-violet-500' },
}

const estadoConfig = {
  activa: { label: 'Activa', color: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500 animate-pulse' },
  pausada: { label: 'Pausada', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  borrador: { label: 'Borrador', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
}

export function CampanasActivas() {
  const [filtroTipo, setFiltroTipo] = useState<'todas' | 'mora' | 'preventiva' | 'reenvio'>('todas')
  const [filtroEstado, setFiltroEstado] = useState<'todas' | 'activa' | 'pausada'>('todas')
  const [historial, setHistorial] = useState<{ nombre: string } | null>(null)
  const [paused, setPaused] = useState<Record<string, boolean>>({})

  const filtradas = campanas.filter(c => {
    if (filtroTipo !== 'todas' && c.tipo !== filtroTipo) return false
    if (filtroEstado !== 'todas') {
      const estado = paused[c.id] ? 'pausada' : c.estado
      if (estado !== filtroEstado) return false
    }
    return true
  })

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Filtros:</span>
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1">
          {([
            { k: 'todas' as const, l: 'Todas' },
            { k: 'mora' as const, l: '● Mora' },
            { k: 'preventiva' as const, l: '● Preventiva' },
            { k: 'reenvio' as const, l: '● Reenvío' },
          ]).map(opt => (
            <button
              key={opt.k}
              onClick={() => setFiltroTipo(opt.k)}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition',
                filtroTipo === opt.k ? 'bg-white text-navy-700 shadow-sm' : 'text-slate-500 hover:text-navy-700'
              )}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1">
          {([
            { k: 'todas' as const, l: 'Todos' },
            { k: 'activa' as const, l: 'Activas' },
            { k: 'pausada' as const, l: 'Pausadas' },
          ]).map(opt => (
            <button
              key={opt.k}
              onClick={() => setFiltroEstado(opt.k)}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition',
                filtroEstado === opt.k ? 'bg-white text-navy-700 shadow-sm' : 'text-slate-500 hover:text-navy-700'
              )}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-slate-500">
          <span className="font-semibold text-navy-700">{filtradas.length}</span> de {campanas.length} campañas
        </div>
      </div>

      {filtradas.length === 0 ? (
        <EmptyState title="Sin campañas" description="Ajustá los filtros o creá una nueva campaña." />
      ) : (
        <div className="space-y-4">
          {filtradas.map(c => {
            const tipo = tipoConfig[c.tipo]
            const estadoActual = paused[c.id] ? 'pausada' : c.estado
            const estado = estadoConfig[estadoActual]
            const vigenciaTxt = c.vigenciaHasta === 'siempre'
              ? 'Siempre ∞'
              : `${c.vigenciaDesde ?? ''} → ${c.vigenciaHasta ?? ''}`
            const proximaTanda = estadoActual === 'activa'
              ? `Hoy ${c.tandasPorDia[0]}`
              : estadoActual === 'pausada' ? '— pausada —' : '—'
            const diasHastaInicio = c.vigenciaDesde ? Math.max(0, Math.floor((new Date(c.vigenciaDesde).getTime() - Date.now()) / 86400000)) : null

            return (
              <article key={c.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition">
                <div className="flex items-start gap-4">
                  <div className={cn('w-14 h-14 shrink-0 rounded-xl bg-gradient-to-br flex items-center justify-center', tipo.ring)}>
                    {c.tipo === 'mora' ? <AlertTriangle className="w-6 h-6" />
                      : c.tipo === 'preventiva' ? <CalendarCheck className="w-6 h-6" />
                      : <Rocket className="w-6 h-6" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="font-display text-lg text-navy-700 leading-tight">{c.nombre}</h3>
                        {c.descripcion && <p className="text-xs text-slate-500 mt-0.5">{c.descripcion}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Chip className={tipo.color}>
                          <span className={cn('inline-block w-1.5 h-1.5 rounded-full mr-1', tipo.dot)} />
                          {tipo.label}
                        </Chip>
                        <PriorityChip prioridad={c.prioridad} />
                        <Chip className={estado.color}>
                          <span className={cn('inline-block w-1.5 h-1.5 rounded-full mr-1', estado.dot)} />
                          {estado.label}
                        </Chip>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-100">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Próxima tanda
                        </div>
                        <div className="font-mono text-sm font-semibold text-navy-700 mt-0.5">{proximaTanda}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Tandas/día</div>
                        <div className="font-mono text-sm font-semibold text-navy-700 mt-0.5">
                          {c.tandasPorDia.length} <span className="text-slate-400 text-xs">({c.tandasPorDia.join('/')})</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Hoy
                        </div>
                        <div className="font-mono text-sm font-semibold text-navy-700 mt-0.5">
                          {c.stats.personasHoy.toLocaleString('es-EC')} personas
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" /> Respondió hoy
                        </div>
                        <div className="font-mono text-sm font-semibold text-emerald-600 mt-0.5">
                          {c.stats.respondieronHoy} <span className="text-slate-400 text-xs">
                            ({c.stats.personasHoy > 0 ? Math.round((c.stats.respondieronHoy / c.stats.personasHoy) * 100) : 0}%)
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Vigencia</div>
                        <div className="font-mono text-sm font-semibold text-navy-700 mt-0.5 flex items-center gap-1">
                          {c.vigenciaHasta === 'siempre' ? (
                            <>Siempre <InfIcon className="w-3.5 h-3.5" /></>
                          ) : (
                            <>{diasHastaInicio !== null && diasHastaInicio > 0 ? `en ${diasHastaInicio}d` : vigenciaTxt}</>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-4 border-t border-slate-100">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Filtros:</span>
                      {Object.entries(c.filtros).map(([k, v]) => (
                        <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-[11px] text-slate-600 font-mono">
                          <span className="text-slate-400">{k}:</span>
                          <span className="text-navy-700 font-semibold">{v}</span>
                        </span>
                      ))}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-navy-50 text-[11px] text-navy-700 font-mono">
                        template: <span className="font-semibold">{c.template.name}</span>
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0 min-w-[150px]">
                    <Button variant="outline" size="sm" onClick={() => setHistorial({ nombre: c.nombre })}>
                      <History className="w-3.5 h-3.5" /> Ver tandas
                    </Button>
                    <Button
                      variant={estadoActual === 'activa' ? 'outline' : 'amber'}
                      size="sm"
                      onClick={() => setPaused(p => ({ ...p, [c.id]: !p[c.id] }))}
                    >
                      {estadoActual === 'activa' ? (<><Pause className="w-3.5 h-3.5" /> Pausar</>) : (<><Play className="w-3.5 h-3.5" /> Reanudar</>)}
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Copy className="w-3.5 h-3.5" /> Duplicar
                    </Button>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}

      <article className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-2 border-dashed border-violet-200 rounded-2xl p-6 text-center">
        <div className="inline-flex w-12 h-12 bg-violet-100 rounded-xl items-center justify-center mb-2 text-violet-600">
          <Rocket className="w-6 h-6" />
        </div>
        <h3 className="font-display text-lg text-violet-900">Próximamente · Generador de templates Meta</h3>
        <p className="text-sm text-violet-700/80 max-w-xl mx-auto mt-1">
          Vas a poder crear tus propios templates desde acá, enviarlos a Meta para aprobación y usarlos en campañas sin salir del dashboard.
        </p>
      </article>

      {historial && (
        <HistorialDrawer
          open={!!historial}
          onClose={() => setHistorial(null)}
          campanaNombre={historial.nombre}
        />
      )}
    </div>
  )
}
